import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { reactive, ref, computed, PropType } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import LevelType from '../../components/LevelType.vue'
import SafetyTags from '@/components/safety-tags/safety-tags.vue'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import {
  RansomwareService,
  RequestParams,
  EventListItem,
} from './../../service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  FormSize,
  FormType,
  ObjectFormItem,
  DateType,
  ActionType,
} from '@/components/cmForm/types'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import {
  computerInfoCols,
  trProtectionDesc,
  trBlockStatus,
  protectionTypeOptions,
  riskLevelOptions,
  blockStatusFilterOptions,
} from '../../types/ransomware-incident'
import dayjs from 'dayjs'
import { useParseTime } from '@/hooks'
import { formatTimeStamp } from '@/utils/time'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { listCol } from '@/utils/list-columns-i18n'

type RansomwareListQueryParams = Partial<RequestParams> & { status_filter?: number }

/** 下拉「已阻止」与统计卡点击一致：请求 statuses 含 已阻止(0)+阻止并结束(2) */
type ExcludeRequestParams = Exclude<keyof RansomwareListQueryParams, 'limit' | 'page'>

/** 近七天：今日往前 6 天 ～ 今日，与 useParseTime 一致 */

export default /*#__PURE__*/_defineComponent({
  __name: 'ransomware-incident-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  hasPermission: {
    type: Boolean,
    default: true
  },
  flag: {
    type: Boolean,
    default: true
  },
  device_id: {
    type: String,
  },
  /** 全量侧栏：多状态（如 已阻止 + 阻止并结束），请求 `statuses: [0, 2]` */
  initialStatusList: {
    type: Array as PropType<number[]>,
    default: () => [] as number[],
  },
  /** 为 true 时全屏列表不设置默认时间（start/end/date 为空，与统计卡点击侧栏一致） */
  initialDateEmpty: {
    type: Boolean,
    default: false,
  },
  /** 全量侧栏：按防护类型（defence_type）预筛；重置时回到该值 */
  initialDefenceType: {
    type: Number,
    default: undefined,
  },
  /**
   * 全量侧栏：与阶段排行图时间选择一致的起止时间（Unix 秒，同 getTimeRangeByEnum）。
   * 在 initialDateEmpty 为 false 且传入有效二元组时优先于默认「近三个月」。
   */
  initialTimeRangeUnix: {
    type: Array as unknown as PropType<[number, number] | undefined>,
    default: undefined,
  },
  /**
   * 为 true 时，在未使用 initialDateEmpty、且未传入有效 initialTimeRangeUnix 的场景下，
   * 默认/重置时间用「近七天」（含今日共 7 个自然日），优先于默认近三个月；预览列表同理会带近七天查询。
   */
  lastSeven: {
    type: Boolean,
    default: false
  },
},
  emits: ["viewAll", "dataUpdate", "detail", "rawData"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props

const emits = __emit

const { t, locale } = useI18n()
const injector = useReflectiveInjector([RansomwareService])
const ransomwareService = injector.get(RansomwareService)

const STATUSES_BLOCKED_AND_ENDED: readonly number[] = [0, 2]

function isBlockedComboStatuses (arr: number[] | undefined): boolean {
  if (!Array.isArray(arr) || arr.length !== 2) return false
  const u = new Set(arr.map((x) => Number(x)))
  return u.size === 2 && u.has(0) && u.has(2)
}

/** 将当前 statuses 映射为筛选项展示值：[0,2] → 选「已阻止」(0) */
function statusFilterUiValue (arr: number[] | undefined): number | undefined {
  if (isBlockedComboStatuses(arr)) return 0
  if (Array.isArray(arr) && arr.length === 1) return Number(arr[0])
  return undefined
}

// 包装 API 以捕获响应数据（去掉仅 UI 的 status_filter，仅传 statuses）
const dataApi = async (raw: RansomwareListQueryParams) => {
  const { status_filter: _sf, ...rest } = raw
  const response = await ransomwareService.getRansomwareProtectList(rest as RequestParams)
  // 将响应数据传递给父组件
  emits('dataUpdate', response)
  // 确保返回正确的数据结构，包含 list 字段
  return {
    list: response.list || [],
    total: response.total || 0,
    blocked_count: response.blocked_count || 0,
    statistics: response.statistics || []
  }
}

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const total = computed(() => tableRef.value?.total || 0)

function getLastSevenRange (): { date: [string, string]; start: number | undefined; end: number | undefined } {
  const range = [dayjs().subtract(6, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
  const [s, e] = useParseTime(range)
  return { date: range, start: s, end: e }
}

// full：initialDateEmpty / initialTimeRangeUnix 为明确上下文优先；否则 lastSeven 覆盖默认近三个月；preview 仅 lastSeven 时带默认区间
function resolveFullInitialTime():
  | { date: [string, string] | undefined; start: number | undefined; end: number | undefined }
  | null {
  if (props.viewMode !== TableViewMode.full) return null
  if (props.initialDateEmpty) {
    return { date: undefined, start: undefined, end: undefined }
  }
  const unix = props.initialTimeRangeUnix
  if (
    unix &&
    unix.length === 2 &&
    unix[0] != null &&
    unix[1] != null
  ) {
    const [a, b] = unix
    return {
      date: [dayjs.unix(a).format('YYYY-MM-DD'), dayjs.unix(b).format('YYYY-MM-DD')] as [string, string],
      start: a,
      end: b,
    }
  }
  if (props.lastSeven) {
    return getLastSevenRange()
  }
  const range = [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
  const [s, e] = useParseTime(range)
  return { date: range, start: s, end: e }
}

const fullInitial = resolveFullInitialTime()
const previewLastSevenInitial =
  props.viewMode === TableViewMode.preview && props.lastSeven ? getLastSevenRange() : null
const initialDateRange =
  fullInitial != null ? fullInitial.date : previewLastSevenInitial != null ? previewLastSevenInitial.date : undefined
const [initialStartTime, initialEndTime] =
  fullInitial != null
    ? [fullInitial.start, fullInitial.end]
    : previewLastSevenInitial != null
      ? [previewLastSevenInitial.start, previewLastSevenInitial.end]
      : useParseTime(initialDateRange || [])

const initialDefence =
  props.initialDefenceType !== undefined && props.initialDefenceType !== null
    ? props.initialDefenceType
    : undefined

const initialStatuses =
  props.initialStatusList.length > 0 ? ([...props.initialStatusList] as number[]) : undefined

const queryParams = reactive<RansomwareListQueryParams>({
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  device_id: props.device_id,
  group_id: -1,
  defence_type: initialDefence,
  start_time: initialStartTime,
  end_time: initialEndTime,
  date: initialDateRange,
  statuses: initialStatuses,
  status_filter: statusFilterUiValue(initialStatuses),
})

const searchKeyword = ref('')

const filterFormColumns = computed<ObjectFormItem<RansomwareListQueryParams, ExcludeRequestParams>>(() => {
  void locale.value
  return {
    group_id: {
      colOpt: { style:'flex:0 0 150px' },
      slot: true,
      label: t('ransomwareIncident.group'),
      outSearchFiledFlag: true,
      hiddenAllLabel: true,
      type: FormType.Select,
      events: {
        change: () => void refreshList(),
      },
    },
    defence_type: {
      colOpt: { span: 12 },
      label: t('ransomwareIncident.riskType'),
      type: FormType.Select,
      dictData: protectionTypeOptions(t),
      events: {
        change: () => void refreshList(),
      },
      selectOpt: {
        clearable: true,
      },
    },
    date: {
      colOpt: { style:'flex: 0 0 260px' },
      label: t('ransomwareIncident.timeRange'),
      type: FormType.Date,
      dateType: DateType.DateRange,
      events: {
        change: (val) => {
            const [start_time, end_time] = useParseTime(val)
            queryParams.start_time = start_time
            queryParams.end_time = end_time
            handleQuery()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('ransomwareIncident.startPlaceholder'),
        'end-placeholder': t('ransomwareIncident.endPlaceholder'),
        clearable: true,
      },
    },
    level: {
      colOpt: { span: 12 },
      label: t('ransomwareIncident.level'),
      type: FormType.Select,
      dictData: riskLevelOptions(t),
      selectOpt: {
        clearable: true,
      },
    },
    status_filter: {
      prop: 'status_filter',
      colOpt: { span: 12 },
      label: t('ransomwareIncident.status'),
      type: FormType.Select,
      dictData: blockStatusFilterOptions(t),
      selectOpt: {
        clearable: true,
      },
      events: {
        change: (val: unknown) => {
          if (val === undefined || val === null || val === '') {
            queryParams.statuses =
              props.initialStatusList.length > 0 ? ([...props.initialStatusList] as number[]) : undefined
          } else {
            const n = Number(val)
            queryParams.statuses = n === 0 ? [...STATUSES_BLOCKED_AND_ENDED] as number[] : [n]
          }
          queryParams.status_filter = statusFilterUiValue(queryParams.statuses)
          void handleQuery()
        },
      },
    },
  }
})

async function handleSearch(value: string) {
  searchKeyword.value = value
  queryParams.key_word = value
  await refreshList()
}

async function handleQuery() {
  if (searchKeyword.value) {
    queryParams.key_word = searchKeyword.value
  }

  await refreshList()
}

function handleReset() {
  searchKeyword.value = ''
  queryParams.key_word = ''
  // full：与 resolveFullInitialTime 一致——先 empty / Unix，再 lastSeven，否则近三个月；preview 在 lastSeven 时恢复近七天
  const defaultParams = props.viewMode === TableViewMode.full
    ? props.initialDateEmpty
      ? { start_time: undefined, end_time: undefined, date: undefined }
      : (() => {
          const unix = props.initialTimeRangeUnix
          if (
            unix &&
            unix.length === 2 &&
            unix[0] != null &&
            unix[1] != null
          ) {
            const [a, b] = unix
            return {
              start_time: a,
              end_time: b,
              date: [dayjs.unix(a).format('YYYY-MM-DD'), dayjs.unix(b).format('YYYY-MM-DD')] as [string, string],
            }
          }
          if (props.lastSeven) {
            const r = getLastSevenRange()
            return { start_time: r.start, end_time: r.end, date: r.date }
          }
          const range = [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
          const [s, e] = useParseTime(range)
          return { start_time: s, end_time: e, date: range }
        })()
    : props.lastSeven
      ? (() => {
          const r = getLastSevenRange()
          return { start_time: r.start, end_time: r.end, date: r.date }
        })()
      : { start_time: undefined, end_time: undefined, date: undefined }
  Object.assign(queryParams, defaultParams)
  queryParams.statuses =
    props.initialStatusList.length > 0 ? ([...props.initialStatusList] as number[]) : undefined
  queryParams.status_filter = statusFilterUiValue(queryParams.statuses)
  if (
    props.initialDefenceType !== undefined &&
    props.initialDefenceType !== null
  ) {
    queryParams.defence_type = props.initialDefenceType
  }
  void refreshList()
}

async function updateGroupId(groupId: number) {
  queryParams.group_id = groupId
  queryParams.page = 1
  await refreshList()
}

const isShowRawData = ref(false)
const currentRow = ref<any>({})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: listCol(t, 'operation'), width: 160, align: 'left', fixed: 'right' },
    actionButtons: (row: EventListItem) => [
      { name: listCol(t, 'detail'), type: 'detail', opt: { size: FormSize.Small, text: true, type: 'text' } },
      { name: listCol(t, 'dataSource'), type: 'rawData', opt: { size: FormSize.Small, text: true, type: 'text' } },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  const concatList = props.flag ? computerInfoCols(true, props.flag, t) : []
  return {
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding:10px 16px' : 'background: transparent;padding:0',
    containerOpt: { style: 'background: var(--cm-color-card-bg)' },
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    disabled: !props.hasPermission,
    path: 'list',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    rowKey: 'device_id',
    selection: false,
    showTip: false,
    usePagination: false,
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    columns: [
      { label: listCol(t, 'operateTime'), prop: 'operate_time', minWidth: 150, formatter: (_row: any, value: any) => formatTimeStamp(value, 'YYYY-MM-DD HH:mm') },
      ...concatList,
      { label: t('ransomwareIncident.level'), prop: 'level', minWidth: 100 },
      { label: t('ransomwareIncident.threatType'), prop: 'tags', minWidth: 250 },
      { label: t('ransomwareIncident.eventDesc'), prop: 'described', formatter: (row: EventListItem) => trProtectionDesc(t, row.defence_type || 0), 'min-width': 220 },
      { label: listCol(t, 'status'), prop: 'status', minWidth: 130, formatter: (_row: any, value: any) => trBlockStatus(t, value) },
    ],
  }
})

function handleAction({ type, rowItem }: { type: any; rowItem?: EventListItem }) {
  if (type === 'detail' && rowItem) {
    emits('detail', rowItem)
  } else if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem
    isShowRawData.value = true
  }
}

function download(type: 0 | 1) {
  const params: Partial<RequestParams> = {
    device_id: queryParams.device_id,
    group_id: queryParams.group_id,
    defence_type: queryParams.defence_type,
    start_time: queryParams.start_time,
    end_time: queryParams.end_time,
    key_word: queryParams.key_word,
    level: queryParams.level,
    statuses: queryParams.statuses,
  }
  ransomwareService.getRansomwareProtectExport(type, params, t('ransomwareIncident.exportFileName'))
}

/** 原 `firstLoading=hasPermission&&flag` 且 onMounted 再 refresh，在 flag 为 true 时与 CmTable 首载双发；首载仅依赖 CmTable，`flag` 仍只控制搜索栏显隐 */
__expose({
  tableRef,
  getList: () => void refreshList(),
  download,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      firstLoading: props.hasPermission,
      "has-permission": props.hasPermission,
      api: dataApi,
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
      pageOption: {
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        (props.viewMode !== _unref(TableViewMode).preview)
          ? (_openBlock(), _createBlock(SearchFilterBar, {
              key: 0,
              modelValue: queryParams,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams) = $event)),
              "form-columns": filterFormColumns.value,
              disabled: !props.hasPermission,
              "show-search": props.flag,
              "search-placeholder": _ctx.$t('ransomwareIncident.searchPlaceholder'),
              "popover-threshold": 1,
              onSearch: handleSearch,
              onQuery: handleQuery,
              onReset: handleReset
            }, {
              group_id: _withCtx(() => [
                _createVNode(GroupTreeSelector, {
                  disabled: !props.hasPermission,
                  "terminal-type": [_unref(TerminalType).windows, _unref(TerminalType).windowsServer],
                  onSelected: updateGroupId
                }, null, 8, ["disabled", "terminal-type"])
              ]),
              actions: _withCtx(() => [
                _createVNode(ExportDropdown, {
                  "has-permission": props.hasPermission,
                  "csv-i18n-key": "ransomwareIncident.exportCsv",
                  "excel-i18n-key": "ransomwareIncident.exportExcel",
                  onExport: download
                }, null, 8, ["has-permission"])
              ]),
              _: 1
            }, 8, ["modelValue", "form-columns", "disabled", "show-search", "search-placeholder"]))
          : _createCommentVNode("", true)
      ]),
      level: _withCtx(({ value }) => [
        _createVNode(LevelType, { type: value }, null, 8, ["type"])
      ]),
      tags: _withCtx(({ value }) => [
        (value && value.length)
          ? (_openBlock(), _createBlock(SafetyTags, {
              key: 0,
              data: value,
              max: 2
            }, null, 8, ["data"]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createTextVNode(" -- ")
            ], 64))
      ]),
      _: 1
    }, 8, ["tableOpt", "tableActOpt", "firstLoading", "has-permission", "modelValue"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})